import { IManifest, ISegment } from 'm3u8-parser';

import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { EDebuggableModuleState, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { EIndependenceType } from '@fragmentsQueue/IFragmentsQueue';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { BasePlaylistRotationController } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { IFragmentLoadingErrorPayload } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import { ELoadingState } from '@streamLoader/net/INetworkLoader';
import { NetworkLoader } from '@streamLoader/net/NetworkLoader';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

enum EFragmentReadinessState {
  NONE,
  LOADING,
  LOADED,
  ERROR,
}

export interface IVODFragment {
  url: string;
  fragmentStartTS: number;
  fragmentEndTS: number;
  readinessState: EFragmentReadinessState;
  fragmentIndex: number;
  isLast: boolean;
  data?: ArrayBuffer;
}

export class VODPlaylistRotationController extends BasePlaylistRotationController {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  private _fragments: IVODFragment[] = [];
  private _lastHandledFragments: string[] = [];
  private _chunksRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private _isDurationPositive: boolean = false;

  private readonly _onFragmentLoadedListener: string;
  private readonly _onFragmentAbortedListener: string;
  private readonly _onFragmentErrorListener: string;
  private readonly _onBufferMetricsAvailableListener: string;

  constructor(eventBus: TEventBus, url: string, startManifest?: IHydratedManifest) {
    super('VODPlaylistRotationController', 'playlistLoader', eventBus);

    this._url = url;

    this._onFragmentLoadedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoaded,
      this._onFragmentLoaded,
    );

    this._onFragmentAbortedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingAborted,
      this._onFragmentLoadingError,
    );
    this._onFragmentErrorListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingError,
      this._onFragmentLoadingError,
    );

    this._onBufferMetricsAvailableListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );

    if (startManifest) {
      this._handleParsedPlaylist(startManifest);
    } else {
      this._loadNextPlaylist();
    }
  }

  @bind
  public destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onFragmentLoadedListener);
    this._eventBus.unsubscribe(this._onBufferMetricsAvailableListener);
    this._eventBus.unsubscribe(this._onFragmentErrorListener);
    this._eventBus.unsubscribe(this._onFragmentAbortedListener);
  }

  public setURL(url: string, soft: boolean): void {
    if (soft) {
      this._url = url;
      return;
    }
    this._logger.info(`Setting new url ${url}`);
    this._currentPlaylistLoader?.destroy();
    this._url = url;
    this._loadNextPlaylist();
  }

  public abortCurrentPlaylistLoading(): void {
    this._logger.info('Aborting current loading playlist');
    this._currentPlaylistLoader?.destroy();
  }

  @bind
  public _handleConfigUpdated(): void {}

  protected _loadNextPlaylist(): void {
    const { playlistLoadingTimeout, baseVODPlaylistRetryTimeout, maxVODPlaylistLoadingRetries } = this.config;

    this._logger.debug('Loading VOD playlist');

    if (this._currentPlaylistLoader) {
      this._currentPlaylistLoader.cancel(`Another playlist with url ${this._url} scheduled to load`);
    }

    this._currentPlaylistLoader = new NetworkLoader(
      {
        url: this._url || '',
        loadingTimeout: playlistLoadingTimeout,
        autoLoad: true,
        mode: 'text',
        onStateChanged: this._onPlaylistLoadingStateChanged,
        baseRetryTimeout: baseVODPlaylistRetryTimeout,
        maxRetries: maxVODPlaylistLoadingRetries,
        workerMode: this.config.workerMode,
      },
      this._eventBus,
    );
  }

  @bind
  protected _onPlaylistLoadingStateChanged(prevState: ELoadingState, nextState: ELoadingState): void {
    if (!this._currentPlaylistLoader || [ELoadingState.ABORTED, ELoadingState.ERROR].includes(nextState)) {
      this.state = EDebuggableModuleState.ERROR;
      this.additionalInfo = {
        playlistLoadError: this._currentPlaylistLoader?.error,
      };
      this._logger.warn(
        `Playlist loader reported ${nextState === ELoadingState.ERROR ? 'error' : 'as aborted'} (${
          this._currentPlaylistLoader?.error?.statusText || 'no error'
        } with status ${this._currentPlaylistLoader?.error?.statusText || 'no status'}). Planning next load`,
      );

      this._currentPlaylistLoader = null;

      return;
    }

    if (nextState !== ELoadingState.LOADED) {
      return;
    }

    const data: string = this._currentPlaylistLoader.data as string;

    this._currentPlaylistLoader = null;

    const manifest: IHydratedManifest | null = this._parsePlaylist(data);
    if (!manifest) {
      this._eventBus.publish(ResolutionsManagerEvents.requestRestartFromMain, {});
      return;
    }

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistInfo, manifest);

    this._handleParsedPlaylist(manifest);
  }

  protected _handleParsedPlaylist(manifest: IManifest): void {
    if (this._isMultiVariantPlaylist(manifest)) {
      this._handleMultivariantManifestDetected(manifest);

      return;
    }

    if (this._isLowLatencyPlaylist(manifest) && this.config.lowLatencyMode) {
      this._handleLLManifestDetected(manifest);

      return;
    }

    if (this._isSimplePlaylist(manifest)) {
      this._handleSimplePlaylistDetected(manifest);

      return;
    }

    this.status = 'Playlist loaded';

    this._populateVODFragments(manifest.segments);

    // Load first segments
  }

  @bind
  protected _planNextLoad(): void {
    return;
  }

  private _populateVODFragments(segments: ISegment[]): void {
    let lastTimePoint: number = 0;

    for (let i: number = 0; i < segments.length; i++) {
      const segment: ISegment = segments[i];

      const fragment: IVODFragment = {
        url: segment.uri,
        fragmentIndex: i,
        fragmentStartTS: lastTimePoint,
        fragmentEndTS: lastTimePoint + segment.duration,
        readinessState: EFragmentReadinessState.NONE,
        isLast: i === segments.length - 1,
      };

      this._fragments.push(fragment);

      lastTimePoint = fragment.fragmentEndTS;
    }

    this._isDurationPositive = lastTimePoint > 0;

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.vodFragmentsInfo, this._fragments);
  }

  private _loadFragment(fragments: IVODFragment[]): void {
    const requestedUrls: string[] = fragments.map(({ url }) => url);

    const loadedFragments: IVODFragment[] = fragments.filter(
      ({ readinessState, url }) => readinessState === EFragmentReadinessState.LOADED && requestedUrls.includes(url),
    );

    loadedFragments.forEach((fragment) => {
      this._logger.info(`Some fragments already loaded, reemitting it: ${fragment.fragmentIndex}`);
      this._eventBus.publish(BasePlaylistRotationControllerEvents.fragments.requestReleaseFromCache, { msn: fragment.fragmentIndex });
    });

    const fragmentsToLoad: IVODFragment[] = fragments.filter(({ readinessState }) =>
      [EFragmentReadinessState.NONE].includes(readinessState),
    );

    fragmentsToLoad.forEach((fragment) => (fragment.readinessState = EFragmentReadinessState.LOADING));

    this._handleFragmentsDetected(
      fragmentsToLoad.map(
        (fragment) =>
          new Fragment(
            {
              msn: fragment.fragmentIndex,
              playlistUrl: this._url || '',
              fragmentUrl: fragment.url,
              pn: 0,
              independent: EIndependenceType.INDEPENDENT,
              duration: fragment.fragmentEndTS - fragment.fragmentStartTS,
              requestedLoadingTimeout: this.config.disableTimeoutsInVOD ? -1 : this.config.baseVODFragmentRetryTimeout,
              startTs: fragment.fragmentStartTS,
              endTs: fragment.fragmentEndTS,
              isTransmuxingBypassRequested: fragment.url.endsWith('.mp4'),
            },
            this._eventBus,
            this.config.shouldLogFragmentsHistory,
          ),
      ),
    );

    this._lastHandledFragments = requestedUrls;
  }

  @bind
  private _onFragmentLoaded(fragment: Fragment): void {
    this._logger.info(`Fragment ${fragment.msn} loaded`);
    const vodFragment: IVODFragment | undefined = this._fragments.find(({ fragmentIndex }) => fragmentIndex === fragment.msn);
    if (!vodFragment) {
      return;
    }

    vodFragment.readinessState = EFragmentReadinessState.LOADED;
    vodFragment.data = fragment.data as IVODFragment['data'];

    this.additionalInfo.fragments = this._fragments.map((fragment) => ({ ...fragment, data: undefined }));
  }

  @bind
  private _onFragmentLoadingError(fragment: IFragmentLoadingErrorPayload): void {
    const vodFragment: IVODFragment | undefined = this._fragments.find(({ fragmentIndex }) => fragmentIndex === fragment.msn);
    if (!vodFragment) {
      return;
    }

    vodFragment.readinessState = EFragmentReadinessState.ERROR;

    if (!this._chunksRetryTimeout) {
      this._chunksRetryTimeout = globalThis.setTimeout(this._retryFragments, this.config.baseVODFragmentRetryTimeout);
    }
  }

  @bind
  private _retryFragments(): void {
    this._chunksRetryTimeout = null;

    const fragmentsToRetry: IVODFragment[] = this._fragments.filter(
      ({ readinessState }) => readinessState === EFragmentReadinessState.ERROR,
    );

    fragmentsToRetry.forEach((frag) => (frag.readinessState = EFragmentReadinessState.NONE));
  }

  @bind
  private _onBufferMetricsAvailable(metrics: IBufferMetrics): void {
    if (this._fragments.some(({ readinessState }) => readinessState === EFragmentReadinessState.LOADING) || !this._isDurationPositive) {
      return;
    }

    let nextFragmentsToLoad: IVODFragment[] = [];
    const { currentTimeRange, currentPlaybackPosition, isInLastBufferedRange, bufferedTimeRanges } = metrics;

    const isNextTimeRangeEmpty: boolean =
      isInLastBufferedRange ||
      (!!currentTimeRange &&
        !bufferedTimeRanges.some(({ start, end }) => start <= currentTimeRange.end + 1 && currentTimeRange.end + 1 <= end));

    const targetTime: number = currentPlaybackPosition + this.config.VODLoadNextSegmentThreshold / 1000;

    if (
      currentTimeRange &&
      currentTimeRange.end - currentPlaybackPosition < this.config.VODLoadNextSegmentThreshold / 1000 &&
      isNextTimeRangeEmpty &&
      targetTime < metrics.knownDuration - 0.1
    ) {
      nextFragmentsToLoad = this._findNextFragmentsByTime(currentPlaybackPosition, targetTime).filter(
        ({ fragmentStartTS, fragmentEndTS }) => !(fragmentStartTS <= currentPlaybackPosition && currentPlaybackPosition <= fragmentEndTS),
      );
    }

    if (!currentTimeRange) {
      nextFragmentsToLoad = this._findNextFragmentsByTime(currentPlaybackPosition, currentPlaybackPosition);
    }

    if (nextFragmentsToLoad.length) {
      this._logger.info(`Loading following fragments ${nextFragmentsToLoad.map(({ fragmentIndex }) => fragmentIndex).join(',')}`);
      const startTsOfPeriod: number = Math.min(...nextFragmentsToLoad.map(({ fragmentStartTS }) => fragmentStartTS));
      const endTsOfPeriod: number = Math.max(...nextFragmentsToLoad.map(({ fragmentEndTS }) => fragmentEndTS));

      this._eventBus.publish(BasePlaylistRotationControllerEvents.fragments.periodFragmentsFound, { startTsOfPeriod, endTsOfPeriod });

      this._loadFragment(nextFragmentsToLoad);
    }
  }

  private _findNextFragmentsByTime(currentTime: number, targetTime: number): IVODFragment[] {
    return this._fragments.filter(
      ({ fragmentEndTS, fragmentStartTS }) =>
        (fragmentStartTS <= currentTime && currentTime <= fragmentEndTS) || // Current fragment
        (fragmentStartTS >= currentTime && targetTime >= fragmentEndTS) || // Fragment is in range
        (fragmentStartTS <= targetTime && targetTime <= fragmentEndTS), // Last fragment
    );
  }
}
